import React, { useState, useEffect, useCallback, useRef } from 'react';
import DatePicker, { registerLocale } from 'react-datepicker';
import ko from 'date-fns/locale/ko';
import { format, subDays, subHours, subMinutes, startOfMonth, endOfMonth, startOfYear, endOfYear, setHours, setMinutes } from 'date-fns';
import "react-datepicker/dist/react-datepicker.css";
import "./DatePickerRenderer.css";

// 한국어 로케일 등록
registerLocale('ko', ko);

/**
 * DatePickerRenderer 컴포넌트
 * 날짜 선택 차트 컴포넌트로, 선택된 날짜를 변수로 발행합니다.
 * 
 * 간소화된 옵션:
 * - mode: 'point' | 'range' (시점 선택 또는 기간 선택)
 * - unit: 'year' | 'month' | 'day' | 'hour' | 'minute' (시간 단위)
 * 
 * @param {Object} props
 * @param {Object} props.chartData - 차트 데이터 및 옵션
 * @param {Function} props.onChartClick - 날짜 선택 시 변수 발행을 위한 콜백
 */
const DatePickerRenderer = ({ 
  chartData, 
  onChartClick,
  style: propsStyle = {},
  className = '',
  forceResize = 0,
  isEditMode = false
}) => {
  // ===== 모든 Hook을 최상단에 선언 (Rules of Hooks) =====
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const containerRef = useRef(null);
  // 프리셋 활성화 상태: 사용자가 프리셋 버튼으로 범위를 선택했는지 표시
  const [activePreset, setActivePreset] = useState(null);
  
  // Range 모드(연/월 단위)에서 시작/종료를 항상 노출하기 위한 별도 상태
  const [rangeStartYear, setRangeStartYear] = useState(null);
  const [rangeStartMonth, setRangeStartMonth] = useState(null);
  const [rangeEndYear, setRangeEndYear] = useState(null);
  const [rangeEndMonth, setRangeEndMonth] = useState(null);
  
  // 옵션 추출
  const options = chartData?.options || {};
  const advanced = options.advanced || {};
  const styleOptions = propsStyle && Object.keys(propsStyle).length > 0 ? propsStyle : (options.style || {});
  
  // 간소화된 핵심 옵션
  const mode = advanced.mode || 'range'; // 'point' | 'range'
  const unit = advanced.unit || 'day'; // 'year' | 'month' | 'day' | 'hour' | 'minute'
  
  // 선택적 옵션
  const defaultValue = advanced.defaultValue || null;
  const minDate = advanced.minDate ? new Date(advanced.minDate) : null;
  const maxDate = advanced.maxDate ? new Date(advanced.maxDate) : null;
  // 기간 선택 빠른 버튼 표시 여부(신규 표준: showQuickButtons, 레거시: showPresets)
  const legacyShowPresets = advanced.showPresets !== false; // 기본값: true (레거시)
  const showPresetButtons = (typeof advanced.showQuickButtons === 'boolean') 
    ? advanced.showQuickButtons 
    : legacyShowPresets;
  const orientation = advanced.orientation || 'vertical'; // 'vertical' | 'horizontal'
  const isHorizontal = orientation === 'horizontal';
  const inputFieldWidth = advanced.inputFieldWidth || 'auto'; // 입력 필드 너비 설정
  // 개별 버튼 표시 설정 (기본적으로 표시, false일 때만 숨김)
  const presetButtons = advanced.presetButtons || {};
  const showBtnToday = presetButtons.today !== false;
  const showBtnYesterday = presetButtons.yesterday !== false;
  const showBtnLast7Days = presetButtons.last7Days !== false;
  const showBtnLast30Days = presetButtons.last30Days !== false;
  const showBtnThisMonth = presetButtons.thisMonth !== false;
  const showBtnThisYear = presetButtons.thisYear !== false;
  
  // unit에 따른 자동 설정
  const getDateFormat = () => {
    switch(unit) {
      case 'year': return 'yyyy';
      case 'month': return 'yyyy-MM';
      case 'day': return 'yyyy-MM-dd';
      case 'hour': return 'yyyy-MM-dd HH:00';
      case 'minute': return 'yyyy-MM-dd HH:mm';
      default: return 'yyyy-MM-dd';
    }
  };
  
  const getPlaceholder = () => {
    const unitText = {
      year: '연도',
      month: '월',
      day: '날짜',
      hour: '시간',
      minute: '시간'
    }[unit] || '날짜';
    
    return mode === 'range' 
      ? `${unitText} 범위를 선택하세요`
      : `${unitText}를 선택하세요`;
  };
  
  const dateFormat = getDateFormat();
  const placeholder = getPlaceholder();
  const showTime = unit === 'hour' || unit === 'minute';
  const timeIntervals = unit === 'hour' ? 60 : 15; // hour는 60분 간격, minute는 15분 간격
  
  // 날짜 변수 발행 함수
  const publishDateVariables = useCallback((start, end) => {
    if (!onChartClick) return;
    
    // unit에 따른 포맷 결정
    const formatString = getDateFormat();
    
    const event = {
      type: 'date_picker',
      mode: mode,
      unit: unit,
      value: mode === 'point' ? (start ? format(start, formatString) : null) : null,
      startValue: mode === 'range' ? (start ? format(start, formatString) : null) : null,
      endValue: mode === 'range' ? (end ? format(end, formatString) : null) : null,
      // 하위 호환성을 위한 필드
      startDate: start ? format(start, 'yyyy-MM-dd') : null,
      endDate: end ? format(end, 'yyyy-MM-dd') : null
    };
    
    onChartClick(event, 'date_select');
  }, [onChartClick, mode, unit]);
  
  // 날짜 선택 핸들러
  const handleDateChange = useCallback((dates) => {
    // 편집 모드에서는 날짜 선택 비활성화
    if (isEditMode) return;
    
    if (mode === 'range') {
      const [start, end] = Array.isArray(dates) ? dates : [dates, null];
      setStartDate(start);
      setEndDate(end);
      // 수동 날짜 변경 시 프리셋 선택 해제
      if (activePreset !== null) setActivePreset(null);
    } else {
      setSelectedDate(dates);
      if (activePreset !== null) setActivePreset(null);
    }
  }, [mode, activePreset, isEditMode]);
  
  // 연도 선택 핸들러
  const handleYearChange = useCallback((year) => {
    const date = new Date(year, 0, 1);
    if (mode === 'range') {
      if (!startDate) {
        setStartDate(date);
      } else if (!endDate) {
        const endOfYear = new Date(year, 11, 31);
        setEndDate(endOfYear);
        publishDateVariables(startDate, endOfYear);
      } else {
        // 새로운 선택 시작
        setStartDate(date);
        setEndDate(null);
      }
    } else {
      setSelectedYear(year);
      publishDateVariables(date, date);
    }
  }, [mode, startDate, endDate, publishDateVariables]);
  
  // 월 선택 핸들러
  const handleMonthChange = useCallback((month) => {
    const year = selectedYear || new Date().getFullYear();
    const date = new Date(year, month - 1, 1);
    if (mode === 'range') {
      // 기존 로직은 시작/종료를 순차 선택하게 했음. 신규 UI에서는 별도 상태를 사용하므로 이 분기는 point에서만 사용됨.
      setStartDate(date);
    } else {
      setSelectedMonth(month);
      publishDateVariables(date, date);
    }
  }, [mode, startDate, endDate, selectedYear, publishDateVariables]);
  
  // 캘린더가 닫힐 때 변수 발행
  const handleCalendarClose = useCallback(() => {
    if (unit === 'day' || unit === 'hour' || unit === 'minute') {
      if (mode === 'range') {
        if (startDate && endDate) {
          publishDateVariables(startDate, endDate);
        }
      } else {
        if (selectedDate) {
          publishDateVariables(selectedDate, selectedDate);
        }
      }
    }
  }, [mode, unit, startDate, endDate, selectedDate, publishDateVariables]);

  // DatePicker 렌더링 함수 (isTransparent와 showFrame를 파라미터로 받음)
  const renderDatePicker = (isTransparent, showFrame) => {
    // unit에 따른 조건부 렌더링
    if (unit === 'year' || unit === 'month') {
      return renderYearMonthPicker();
    }
    
    // Day/Hour/Minute 선택용 DatePicker
    return (
      <div 
        className="date-picker-wrapper"
        style={{ 
          width: inputFieldWidth === 'auto' ? '100%' : inputFieldWidth,
          '--input-font-size': typeof getInputStyle().fontSize === 'number' ? `${getInputStyle().fontSize}px` : getInputStyle().fontSize,
          '--input-font-weight': getInputStyle().fontWeight,
          '--input-font-style': getInputStyle().fontStyle,
          '--input-color': getInputStyle().color,
          '--input-font-family': getInputStyle().fontFamily
        }}>
        {mode === 'range' ? (
          <DatePicker
            selectsRange={true}
            startDate={startDate}
            endDate={endDate}
            onChange={(dates) => {
              if (isEditMode) return;
              const [start, end] = Array.isArray(dates) ? dates : [dates, null];
              setStartDate(start);
              setEndDate(end);
            }}
            onCalendarClose={handleCalendarClose}
            dateFormat={dateFormat}
            showTimeSelect={showTime}
            showTimeInput={unit === 'minute'}
            timeIntervals={timeIntervals}
            placeholderText={placeholder}
            minDate={minDate}
            maxDate={maxDate}
            locale="ko"
            isClearable={true}
            showMonthDropdown
            showYearDropdown
            dropdownMode="select"
            disabled={isEditMode}
            className={`date-picker-input ${isTransparent ? 'transparent-mode' : ''} ${!showFrame ? 'frameless-mode' : ''}`}
            calendarClassName="date-picker-calendar"
            wrapperClassName="date-picker-input-wrapper"
            withPortal={true}
            portalId="modal-root"
            popperPlacement="bottom-start"
          />
        ) : (
          <DatePicker
            selected={selectedDate}
            onChange={(date) => {
              if (isEditMode) return;
              setSelectedDate(date);
            }}
            onCalendarClose={handleCalendarClose}
            dateFormat={dateFormat}
            showTimeSelect={showTime}
            showTimeInput={unit === 'minute'}
            timeIntervals={timeIntervals}
            placeholderText={placeholder}
            minDate={minDate}
            maxDate={maxDate}
            locale="ko"
            isClearable={true}
            showMonthDropdown
            showYearDropdown
            dropdownMode="select"
            selectsStart={false}
            selectsEnd={false}
            disabled={isEditMode}
            className={`date-picker-input ${isTransparent ? 'transparent-mode' : ''} ${!showFrame ? 'frameless-mode' : ''}`}
            calendarClassName="date-picker-calendar"
            wrapperClassName="date-picker-input-wrapper"
            withPortal={true}
            portalId="modal-root"
            popperPlacement="bottom-start"
          />
        )}
      </div>
    );
  };
  
  // 빠른 선택 버튼 핸들러 (unit이 day일 때만 표시)
  const handleQuickSelect = useCallback((preset) => {
    // 편집 모드에서는 빠른 선택 비활성화
    if (isEditMode) return;
    
    const today = new Date();
    let start, end;
    
    switch (preset) {
      case 'today':
        start = end = today;
        break;
      case 'yesterday':
        start = end = subDays(today, 1);
        break;
      case 'last_7_days':
        start = subDays(today, 6);
        end = today;
        break;
      case 'last_30_days':
        start = subDays(today, 29);
        end = today;
        break;
      case 'this_month':
        start = startOfMonth(today);
        end = endOfMonth(today);
        break;
      case 'this_year':
        start = startOfYear(today);
        end = endOfYear(today);
        break;
      default:
        return;
    }
    
    if (mode === 'range') {
      setStartDate(start);
      setEndDate(end);
      publishDateVariables(start, end);
    } else {
      setSelectedDate(start);
      publishDateVariables(start, start);
    }
    // 프리셋 활성화 상태 기록
    setActivePreset(preset);
  }, [mode, publishDateVariables, isEditMode]);
  
  // Year/Month 선택용 렌더링 함수
  const renderYearMonthPicker = () => {
    const currentYear = new Date().getFullYear();
    const years = Array.from({ length: 20 }, (_, i) => currentYear - 10 + i)
      .filter(y => (!minDate || y >= minDate.getFullYear()) && (!maxDate || y <= maxDate.getFullYear()));
    const months = Array.from({ length: 12 }, (_, i) => i + 1);
    
    // 자동 너비 조정 여부 확인
    const isAutoWidth = inputFieldWidth === 'auto';
    
    // 컨테이너 스타일 설정
    const containerStyle = {
      width: isAutoWidth ? '100%' : inputFieldWidth
    };
    
    // select 박스 스타일 설정
    const selectStyle = {
      ...getInputStyle(),
      ...(isAutoWidth 
        ? { flex: 1, minWidth: 0 } 
        : { width: 'calc(50% - 5px)' }) // auto가 아닐 때는 컨테이너 너비의 절반
    };
    
    if (unit === 'year') {
      // range 모드에서도 항상 시작/종료 연도 셀렉트 2개를 노출
      const startYears = years;
      const endYears = years.filter(y => !rangeStartYear || y >= rangeStartYear);

      const onStartYearChange = (y) => {
        setRangeStartYear(y || null);
        const start = y ? new Date(y, 0, 1) : null;
        setStartDate(start);
        // 종료가 시작보다 과거이면 초기화
        if (rangeEndYear && y && rangeEndYear < y) {
          setRangeEndYear(null);
          setEndDate(null);
        }
      };

      const onEndYearChange = (y) => {
        setRangeEndYear(y || null);
        const end = y ? new Date(y, 11, 31) : null;
        setEndDate(end);
        if (mode === 'range' && startDate && end) {
          publishDateVariables(startDate, end);
        }
      };

      // point 모드에서는 기존 selectedYear 동작 유지
      if (mode === 'point') {
        return (
          <div className="year-month-picker" style={containerStyle}>
            <select 
              value={selectedYear} 
              onChange={(e) => handleYearChange(parseInt(e.target.value))}
              className="year-select"
              style={selectStyle}
              disabled={isEditMode}
            >
              <option value="">연도</option>
              {years.map(year => (
                <option key={year} value={year}>{year}년</option>
              ))}
            </select>
          </div>
        );
      }

      return (
        <div className="year-month-picker" style={containerStyle}>
          <select
            value={rangeStartYear ?? ''}
            onChange={(e) => onStartYearChange(parseInt(e.target.value))}
            className="year-select"
            style={selectStyle}
            disabled={isEditMode}
          >
            <option value="">시작 연도</option>
            {startYears.map(year => (
              <option key={year} value={year}>{year}년</option>
            ))}
          </select>
          <span style={{ margin: '0 10px' }}>~</span>
          <select
            value={rangeEndYear ?? ''}
            onChange={(e) => onEndYearChange(parseInt(e.target.value))}
            className="year-select"
            style={selectStyle}
            disabled={isEditMode || !rangeStartYear}
          >
            <option value="">종료 연도</option>
            {endYears.map(year => (
              <option key={year} value={year}>{year}년</option>
            ))}
          </select>
        </div>
      );
    }
    
    if (unit === 'month') {
      // point 모드에서는 단일 년/월 선택기만 렌더링
      if (mode === 'point') {
        const availableMonths = months.filter(m => {
          if (!selectedYear) return true;
          if (minDate && selectedYear === minDate.getFullYear()) {
            return m >= (minDate.getMonth() + 1);
          }
          if (maxDate && selectedYear === maxDate.getFullYear()) {
            return m <= (maxDate.getMonth() + 1);
          }
          return true;
        });

        const handleYearMonthChange = (year, month) => {
          if (year && month) {
            const selectedDate = new Date(year, month - 1, 1);
            setSelectedDate(selectedDate);
            setSelectedYear(year);
            setSelectedMonth(month);
            
            // point 모드에서는 단일 날짜를 시작/종료 동일하게 발행
            const lastDay = new Date(year, month, 0).getDate();
            const endDate = new Date(year, month - 1, lastDay);
            publishDateVariables(selectedDate, endDate);
          }
        };

        return (
          <div className="year-month-picker" style={containerStyle}>
            <select 
              value={selectedYear || ''} 
              onChange={(e) => {
                if (isEditMode) return;
                const year = parseInt(e.target.value) || null;
                setSelectedYear(year);
                if (year && selectedMonth) {
                  handleYearMonthChange(year, selectedMonth);
                } else {
                  setSelectedMonth(null);
                  setSelectedDate(null);
                }
              }}
              className="year-select"
              style={selectStyle}
              disabled={isEditMode}
            >
              <option value="">연도</option>
              {years.map(year => (
                <option key={year} value={year}>{year}년</option>
              ))}
            </select>
            <select 
              value={selectedMonth || ''} 
              onChange={(e) => {
                if (isEditMode) return;
                const month = parseInt(e.target.value) || null;
                setSelectedMonth(month);
                if (selectedYear && month) {
                  handleYearMonthChange(selectedYear, month);
                } else {
                  setSelectedDate(null);
                }
              }}
              className="month-select"
              style={selectStyle}
              disabled={isEditMode || !selectedYear}
            >
              <option value="">월</option>
              {availableMonths.map(month => (
                <option key={month} value={month}>{month}월</option>
              ))}
            </select>
          </div>
        );
      }

      // range 모드에서는 연/월 각각 시작·종료 두 셋트를 노출
      const startYears = years;
      const startMonths = (y) => months.filter(m => {
        if (!y) return true;
        if (minDate && y === minDate.getFullYear()) {
          return m >= (minDate.getMonth() + 1);
        }
        return true;
      });

      const endYears = years.filter(y => !rangeStartYear || y >= rangeStartYear);
      const endMonths = (y) => months.filter(m => {
        if (!y) return true;
        // 종료는 시작 이상, 그리고 maxDate 제한
        const candidate = new Date(y, m - 1, 1);
        if (startDate && candidate < startDate) return false;
        if (maxDate && y === maxDate.getFullYear()) {
          return m <= (maxDate.getMonth() + 1);
        }
        return true;
      });

      const recomputeStartDate = (y, m) => {
        if (y && m) {
          setStartDate(new Date(y, m - 1, 1));
        } else {
          setStartDate(null);
        }
      };

      const recomputeEndDate = (y, m) => {
        if (y && m) {
          const lastDay = new Date(y, m, 0).getDate();
          setEndDate(new Date(y, m - 1, lastDay));
        } else {
          setEndDate(null);
        }
      };

      const onStartYearChange = (y) => {
        const val = isNaN(y) ? null : y;
        setRangeStartYear(val);
        // 시작 월이 minDate보다 작아질 수 있어 초기화
        setRangeStartMonth(null);
        recomputeStartDate(val, null);
        // 종료가 시작보다 과거이면 초기화
        if (rangeEndYear && val && rangeEndYear < val) {
          setRangeEndYear(null);
          setRangeEndMonth(null);
          setEndDate(null);
        }
      };

      const onStartMonthChange = (m) => {
        const val = isNaN(m) ? null : m;
        setRangeStartMonth(val);
        recomputeStartDate(rangeStartYear, val);
        // 종료가 시작보다 과거이면 종료 초기화
        if (endDate && startDate && endDate < startDate) {
          setRangeEndYear(null);
          setRangeEndMonth(null);
          setEndDate(null);
        }
      };

      const onEndYearChange = (y) => {
        const val = isNaN(y) ? null : y;
        setRangeEndYear(val);
        // 종료 월 초기화 후 다시 계산
        setRangeEndMonth(null);
        recomputeEndDate(val, null);
        // 월이 이미 선택되어 있고 종료 연도만 변경된 경우, 두 값이 모두 있으면 publish
        if (mode === 'range' && startDate && endDate) {
          publishDateVariables(startDate, endDate);
        }
      };

      const onEndMonthChange = (m) => {
        const val = isNaN(m) ? null : m;
        setRangeEndMonth(val);
        recomputeEndDate(rangeEndYear, val);
        // 종료 월 선택으로 범위가 완성되면 즉시 publish
        if (mode === 'range' && rangeEndYear && startDate) {
          const y = rangeEndYear;
          const month = val;
          if (y && month) {
            const lastDay = new Date(y, month, 0).getDate();
            const end = new Date(y, month - 1, lastDay);
            publishDateVariables(startDate, end);
          }
        }
      };

      return (
        <div className="year-month-picker" style={containerStyle}>
          {/* 시작 연 */}
          <select
            value={rangeStartYear ?? ''}
            onChange={(e) => {
              if (isEditMode) return;
              onStartYearChange(parseInt(e.target.value));
            }}
            className="year-select"
            style={selectStyle}
            disabled={isEditMode}
          >
            <option value="">시작 연도</option>
            {startYears.map(year => (
              <option key={year} value={year}>{year}년</option>
            ))}
          </select>
          {/* 시작 월 */}
          <select
            value={rangeStartMonth ?? ''}
            onChange={(e) => {
              if (isEditMode) return;
              onStartMonthChange(parseInt(e.target.value));
            }}
            className="month-select"
            style={selectStyle}
            disabled={isEditMode || !rangeStartYear}
          >
            <option value="">시작 월</option>
            {startMonths(rangeStartYear).map(month => (
              <option key={month} value={month}>{month}월</option>
            ))}
          </select>

          <span style={{ margin: '0 10px' }}>~</span>

          {/* 종료 연 */}
          <select
            value={rangeEndYear ?? ''}
            onChange={(e) => {
              if (isEditMode) return;
              onEndYearChange(parseInt(e.target.value));
            }}
            className="year-select"
            style={selectStyle}
            disabled={isEditMode || !rangeStartYear || !rangeStartMonth}
          >
            <option value="">종료 연도</option>
            {endYears.map(year => (
              <option key={year} value={year}>{year}년</option>
            ))}
          </select>
          {/* 종료 월 */}
          <select
            value={rangeEndMonth ?? ''}
            onChange={(e) => {
              if (isEditMode) return;
              onEndMonthChange(parseInt(e.target.value));
            }}
            className="month-select"
            style={selectStyle}
            disabled={isEditMode || !rangeEndYear || !rangeStartYear || !rangeStartMonth}
          >
            <option value="">종료 월</option>
            {endMonths(rangeEndYear).map(month => (
              <option key={month} value={month}>{month}월</option>
            ))}
          </select>
        </div>
      );
    }
    
    return null;
  };
  
  
  // 스타일 헬퍼 함수들
  const getInputStyle = () => ({
    fontSize: styleOptions?.fonts?.value?.fontSize || 14,
    fontWeight: styleOptions?.fonts?.value?.fontWeight || 'normal',
    fontStyle: styleOptions?.fonts?.value?.fontStyle || 'normal',
    color: styleOptions?.fonts?.value?.color || '#333',
    fontFamily: styleOptions?.fonts?.value?.fontFamily || 'inherit'
  });
  
  const getTitleStyle = () => ({
    fontSize: styleOptions?.fonts?.title?.fontSize || 16,
    fontWeight: styleOptions?.fonts?.title?.fontWeight || '600',
    fontStyle: styleOptions?.fonts?.title?.fontStyle || 'normal',
    color: styleOptions?.fonts?.title?.color || '#333',
    fontFamily: styleOptions?.fonts?.title?.fontFamily || 'inherit',
    lineHeight: styleOptions?.fonts?.title?.lineHeight || 1.4,
    marginBottom: '12px'
  });
  
  const getButtonTextStyle = () => ({
    fontSize: styleOptions?.fonts?.buttonText?.fontSize || 13,
    fontWeight: styleOptions?.fonts?.buttonText?.fontWeight || 'normal',
    fontStyle: styleOptions?.fonts?.buttonText?.fontStyle || 'normal',
    color: styleOptions?.fonts?.buttonText?.color || '#657786',
    fontFamily: styleOptions?.fonts?.buttonText?.fontFamily || 'inherit',
    lineHeight: styleOptions?.fonts?.buttonText?.lineHeight || 1.4,
    cursor: isEditMode ? 'default' : 'pointer',
    opacity: isEditMode ? 0.6 : 1,
    pointerEvents: isEditMode ? 'none' : 'auto'
  });
  
  // ===== useEffect는 모든 Hook 선언 이후에 =====
  
  
  // 초기값 설정
  useEffect(() => {
    if (defaultValue) {
      const date = new Date(defaultValue);
      if (mode === 'point') {
        setSelectedDate(date);
        if (unit === 'year') {
          setSelectedYear(date.getFullYear());
        } else if (unit === 'month') {
          setSelectedYear(date.getFullYear());
          setSelectedMonth(date.getMonth() + 1);
        }
        publishDateVariables(date, date);
      } else {
        // range mode의 경우 defaultValue를 시작점으로 설정
        setStartDate(date);
      }
    }
  }, [defaultValue, mode, unit, publishDateVariables]);

  // 초기값 프리셋 적용 (defaultValue가 없을 때만 1회 적용)
  useEffect(() => {
    if (defaultValue) return; // defaultValue 우선
    const preset = advanced.initialPreset;
    if (!preset) return;
    // 이미 사용자가 값을 선택한 경우 재적용 방지
    if ((mode === 'range' && (startDate || endDate)) || (mode === 'point' && selectedDate)) return;

    const now = new Date();
    const applyRange = (start, end) => {
      setStartDate(start);
      setEndDate(end);
      publishDateVariables(start, end);
    };
    const applyPoint = (date) => {
      setSelectedDate(date);
      // 연/월 단위 포인트의 내부 상태 동기화
      if (unit === 'year') {
        setSelectedYear(date.getFullYear());
      } else if (unit === 'month') {
        setSelectedYear(date.getFullYear());
        setSelectedMonth(date.getMonth() + 1);
      }
      // month point는 끝일을 포함해 발행, 그 외는 동일 시점 발행
      if (unit === 'month') {
        const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
        const end = new Date(date.getFullYear(), date.getMonth(), lastDay);
        publishDateVariables(date, end);
      } else {
        publishDateVariables(date, date);
      }
    };

    if (unit === 'minute' || unit === 'hour' || unit === 'day') {
      if (mode === 'range') {
        switch (preset) {
          case 'last_15_min':
            return applyRange(subMinutes(now, 15), now);
          case 'last_1_hour':
            return applyRange(subHours(now, 1), now);
          case 'last_6_hours':
            return applyRange(subHours(now, 6), now);
          case 'last_24_hours':
            return applyRange(subHours(now, 24), now);
          case 'today':
            setActivePreset('today');
            return applyRange(now, now);
          case 'yesterday':
            setActivePreset('yesterday');
            return applyRange(subDays(now, 1), subDays(now, 1));
          case 'last_7_days':
            setActivePreset('last_7_days');
            return applyRange(subDays(now, 6), now);
          case 'last_30_days':
            setActivePreset('last_30_days');
            return applyRange(subDays(now, 29), now);
          case 'this_month':
            setActivePreset('this_month');
            return applyRange(startOfMonth(now), endOfMonth(now));
          case 'last_month': {
            const prevMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
            const prevMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0);
            return applyRange(prevMonthStart, prevMonthEnd);
          }
          case 'this_year':
            setActivePreset('this_year');
            return applyRange(startOfYear(now), endOfYear(now));
          case 'last_year':
            return applyRange(new Date(now.getFullYear() - 1, 0, 1), new Date(now.getFullYear() - 1, 11, 31));
          default:
            return;
        }
      } else {
        // point
        switch (preset) {
          case 'now':
            return applyPoint(now);
          case 'one_min_ago':
            return applyPoint(subMinutes(now, 1));
          case 'one_hour_ago':
            return applyPoint(subHours(now, 1));
          case 'today':
            setActivePreset('today');
            return applyPoint(now);
          case 'yesterday':
            setActivePreset('yesterday');
            return applyPoint(subDays(now, 1));
          default:
            return;
        }
      }
    }

    if (unit === 'month') {
      if (mode === 'range') {
        switch (preset) {
          case 'this_month':
            setActivePreset('this_month');
            return applyRange(startOfMonth(now), endOfMonth(now));
          case 'last_month': {
            const prevMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
            const prevMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0);
            return applyRange(prevMonthStart, prevMonthEnd);
          }
          default:
            return;
        }
      } else {
        switch (preset) {
          case 'this_month':
            setActivePreset('this_month');
            return applyPoint(new Date(now.getFullYear(), now.getMonth(), 1));
          case 'last_month':
            return applyPoint(new Date(now.getFullYear(), now.getMonth() - 1, 1));
          default:
            return;
        }
      }
    }

    if (unit === 'year') {
      if (mode === 'range') {
        switch (preset) {
          case 'this_year':
            setActivePreset('this_year');
            return applyRange(startOfYear(now), endOfYear(now));
          case 'last_year':
            return applyRange(new Date(now.getFullYear() - 1, 0, 1), new Date(now.getFullYear() - 1, 11, 31));
          default:
            return;
        }
      } else {
        switch (preset) {
          case 'this_year':
            setActivePreset('this_year');
            return applyPoint(new Date(now.getFullYear(), 0, 1));
          case 'last_year':
            return applyPoint(new Date(now.getFullYear() - 1, 0, 1));
          default:
            return;
        }
      }
    }
  }, [advanced.initialPreset, defaultValue, mode, unit, startDate, endDate, selectedDate, publishDateVariables]);
  
  
  // 차트 데이터가 없는 경우 빈 컴포넌트 반환
  if (!chartData) {
    return null;
  }
  

    // 투명 배경 및 프레임 옵션 처리
    const isTransparent = options.transparent === true || options.transparent === 'true';
    const showFrame = options.showFrame === true || options.showFrame === 'true';
    
    const containerStyle = {
      ...propsStyle,
      backgroundColor: isTransparent ? 'transparent' : (styleOptions?.background?.color || propsStyle?.backgroundColor || 'transparent'),
      minHeight: '80px',
      border: showFrame ? '1px solid #e0e0e0' : 'none',
      borderRadius: showFrame ? '8px' : '0',
      padding: '12px',
      boxShadow: 'none',
      position: 'relative'
    };
    
    return (
    <div ref={containerRef} className={`date-picker-container ${isHorizontal ? 'horizontal' : 'vertical'} ${className}`} style={containerStyle}>
      {/* 제목 */}
      {options.showTitle !== false && styleOptions?.visibility?.title !== false && options.title && options.title.trim() !== '' && (
        <div className="date-picker-title" style={getTitleStyle()}>{options.title}</div>
      )}
      
      {/* 가로 배치일 때 버튼과 입력 필드를 감싸는 wrapper */}
      {isHorizontal && unit === 'day' ? (
        <div className="horizontal-wrapper">
          {/* 기간 선택 버튼 - showPresetButtons가 true일 때만 표시 */}
          {showPresetButtons && (
            <div className="date-picker-quick-buttons">
              {showBtnToday && (
                <button onClick={() => handleQuickSelect('today')} className={`quick-btn ${activePreset === 'today' ? 'selected' : ''}`} style={getButtonTextStyle()}>
                  오늘
                </button>
              )}
              {showBtnYesterday && (
                <button onClick={() => handleQuickSelect('yesterday')} className={`quick-btn ${activePreset === 'yesterday' ? 'selected' : ''}`} style={getButtonTextStyle()}>
                  어제
                </button>
              )}
              {mode === 'range' && (
                <>
                  {showBtnLast7Days && (
                    <button onClick={() => handleQuickSelect('last_7_days')} className={`quick-btn ${activePreset === 'last_7_days' ? 'selected' : ''}`} style={getButtonTextStyle()}>
                      최근 7일
                    </button>
                  )}
                  {showBtnLast30Days && (
                    <button onClick={() => handleQuickSelect('last_30_days')} className={`quick-btn ${activePreset === 'last_30_days' ? 'selected' : ''}`} style={getButtonTextStyle()}>
                      최근 30일
                    </button>
                  )}
                  {showBtnThisMonth && (
                    <button onClick={() => handleQuickSelect('this_month')} className={`quick-btn ${activePreset === 'this_month' ? 'selected' : ''}`} style={getButtonTextStyle()}>
                      이번 달
                    </button>
                  )}
                  {showBtnThisYear && (
                    <button onClick={() => handleQuickSelect('this_year')} className={`quick-btn ${activePreset === 'this_year' ? 'selected' : ''}`} style={getButtonTextStyle()}>
                      올해
                    </button>
                  )}
                </>
              )}
            </div>
          )}
          
          {/* DatePicker 입력 필드 */}
          {renderDatePicker(isTransparent, showFrame)}
        </div>
      ) : isHorizontal ? (
        // 가로 배치이지만 day가 아닌 경우 (year, month, hour, minute)
        <div className="horizontal-wrapper">
          {renderDatePicker(isTransparent, showFrame)}
        </div>
      ) : (
        <div className="vertical-wrapper">
          {/* 세로 배치일 때 기간 선택 버튼 */}
          {showPresetButtons && unit === 'day' && (
            <div className="date-picker-quick-buttons">
              {showBtnToday && (
                <button onClick={() => handleQuickSelect('today')} className={`quick-btn ${activePreset === 'today' ? 'selected' : ''}`} style={getButtonTextStyle()}>
                  오늘
                </button>
              )}
              {showBtnYesterday && (
                <button onClick={() => handleQuickSelect('yesterday')} className={`quick-btn ${activePreset === 'yesterday' ? 'selected' : ''}`} style={getButtonTextStyle()}>
                  어제
                </button>
              )}
              {mode === 'range' && (
                <>
                  {showBtnLast7Days && (
                    <button onClick={() => handleQuickSelect('last_7_days')} className={`quick-btn ${activePreset === 'last_7_days' ? 'selected' : ''}`} style={getButtonTextStyle()}>
                      최근 7일
                    </button>
                  )}
                  {showBtnLast30Days && (
                    <button onClick={() => handleQuickSelect('last_30_days')} className={`quick-btn ${activePreset === 'last_30_days' ? 'selected' : ''}`} style={getButtonTextStyle()}>
                      최근 30일
                    </button>
                  )}
                  {showBtnThisMonth && (
                    <button onClick={() => handleQuickSelect('this_month')} className={`quick-btn ${activePreset === 'this_month' ? 'selected' : ''}`} style={getButtonTextStyle()}>
                      이번 달
                    </button>
                  )}
                  {showBtnThisYear && (
                    <button onClick={() => handleQuickSelect('this_year')} className={`quick-btn ${activePreset === 'this_year' ? 'selected' : ''}`} style={getButtonTextStyle()}>
                      올해
                    </button>
                  )}
                </>
              )}
            </div>
          )}
          
          {/* DatePicker 입력 필드 */}
          {renderDatePicker(isTransparent, showFrame)}
        </div>
      )}
      
      {/* 디버그 정보 (개발 모드에서만 표시) */}
      {advanced.debug && (
        <div className="date-picker-debug" style={{ marginTop: '10px', fontSize: '11px', color: '#666' }}>
          <pre>{JSON.stringify({
            mode,
            unit,
            startDate: startDate ? format(startDate, dateFormat) : null,
            endDate: endDate ? format(endDate, dateFormat) : null,
            selectedDate: selectedDate ? format(selectedDate, dateFormat) : null
          }, null, 2)}</pre>
        </div>
      )}
    </div>
  );
};

export default DatePickerRenderer;